import { Entity, EntityRowCsv, EntityTemplateCsv, EntityTypeEnum } from '../../types/entity';
import { Group } from '../../types/group';
import { XeroTenantConnection } from '../../types/xero';
import { OnboardingXeroEntity } from './bulk-onboard-xero-entities/XeroEntitiesForm';
import plural from '../../utilities/plural';

const monthDaysMap: { [key: number]: number } = {
    1: 31,
    2: 29,
    3: 31,
    4: 30,
    5: 31,
    6: 30,
    7: 31,
    8: 31,
    9: 30,
    10: 31,
    11: 30,
    12: 31,
};

/**
 * Check if entity csv file is valid
 *
 * @param rows Data parsed from csv file
 * @returns [{errorMessage}, EntityRow[]]
 */
export function validateData(rows: EntityTemplateCsv[], groups: Group[]): [{ [rowNumber: string]: string[] }, EntityRowCsv[]] {
    const errorMessages: { [rowNumber: string]: string[] } = {};
    const entityRows: EntityRowCsv[] = [];

    const names: string[] = [];
    const legalNames: string[] = [];
    const entityTypes = [
        'COMPANY',
        'TRUST',
        'PARTNERSHIP',
        'SOLE_TRADER',
        'INDIVIDUAL',
        'UNIT_TRUST',
        'ESTATE',
        'SELF_MANAGED_SUPERANNUATION_FUND',
        'SUPERANNUATION_FUND',
        'GOVERNMENT_ENTITY',
        'CLUB_OR_SOCIETY',
        'NOT_FOR_PROFIT',
        'OTHER',
        'ACCOUNTING_PRACTICE',
        'CHARITY',
        'LOOK_THROUGH_COMPANY',
        'S_CORPORATION',
    ];

    for (const row of rows) {
        const rowNumber = `${rows.indexOf(row) + 2}`;
        const messages: string[] = [];

        // Skip empty rows
        if (Object.values(row).every((v) => !v)) {
            continue;
        }

        if (row['Name *'].trim() === '') {
            messages.push(`'Name' field cannot be empty.`);
        }

        if (names.includes(row['Name *'].toLowerCase().trim())) {
            messages.push(`Values in 'Name' field must be unique.`);
        } else {
            names.push(row['Name *'].toLowerCase().trim());
        }

        if (row['Legal Name *'].trim() === '') {
            messages.push(`'Legal Name' field cannot be empty.`);
        }

        if (legalNames.includes(row['Legal Name *'].toLowerCase().trim())) {
            messages.push(`Values in 'Legal Name' field must be unique.`);
        } else {
            legalNames.push(row['Legal Name *'].toLowerCase().trim());
        }

        const endMonth = row['Financial Year End Month *'].trim();
        let monthError = false;
        if (endMonth === '') {
            messages.push(`'Financial Year End Month' field cannot be empty.`);
            monthError = true;
        } else if (Number.isNaN(Number(endMonth))) {
            messages.push(`'Financial Year End Month' field must be a valid number.`);
            monthError = true;
        } else if (!Number.isInteger(Number(endMonth))) {
            messages.push(`'Financial Year End Month' field must be an integer.`);
            monthError = true;
        } else if (Number(endMonth) > 12 || Number(endMonth) < 1) {
            messages.push(`'Financial Year End Month' field must be a value between 1-12.`);
            monthError = true;
        }

        const endDay = row['Financial Year End Day *'].trim();
        if (!monthError) {
            // const daysInMonth = new Date(new Date().getFullYear(), Number(endMonth), 0).getDate();
            const daysInMonth = monthDaysMap[Number(endMonth)];
            if (endDay === '') {
                messages.push(`'Financial Year End Day' field cannot be empty.`);
            } else if (Number.isNaN(Number(endDay))) {
                messages.push(`'Financial Year End Day' field must be a valid number.`);
            } else if (!Number.isInteger(Number(endDay))) {
                messages.push(`'Financial Year End Day' field must be an integer.`);
            } else if (Number(endDay) > daysInMonth || Number(endDay) < 1) {
                messages.push(`'Financial Year End Day' field must be a value between 1-${daysInMonth}.`);
            }
        }

        if (row['Entity Type *'].trim() === '') {
            messages.push(`'Entity Type' field cannot be empty.`);
        }

        if (!entityTypes.includes(row['Entity Type *'].trim().toUpperCase())) {
            messages.push(
                `Values in 'Entity Type' field must be either 'COMPANY', 'TRUST', 'PARTNERSHIP', 'SOLE_TRADER', 'INDIVIDUAL', 'UNIT_TRUST', 'ESTATE', 'SELF_MANAGED_SUPERANNUATION_FUND', 'SUPERANNUATION_FUND', 'GOVERNMENT_ENTITY', 'CLUB_OR_SOCIETY', 'NOT_FOR_PROFIT', 'ACCOUNTING_PRACTICE', 'CHARITY', 'LOOK_THROUGH_COMPANY', 'S_CORPORATION' or 'OTHER'.`
            );
        }
        let groupId: string | undefined;
        if (row.Group) {
            const matchingGroup = groups.find((group) => group.name.toLowerCase() === row.Group.trim().toLowerCase());
            if (!matchingGroup) {
                messages.push(`${row.Group} does not exist.`);
            } else {
                groupId = matchingGroup.id;
            }
        }

        if (messages.length !== 0) {
            errorMessages[rowNumber] = messages;
        } else {
            const entityRow = {
                name: row['Name *'].trim(),
                legalName: row['Legal Name *'].trim(),
                financialYearEndDay: Number(endDay),
                financialYearEndMonth: Number(endMonth),
                type: row['Entity Type *'].toUpperCase() as EntityTypeEnum,
                groups: groupId ? [groupId] : [],
            };
            entityRows.push(entityRow);
        }
    }

    return [errorMessages, entityRows];
}

/**
 * Format number of entities created/updated into string array
 *
 * @param entitiesCreated Number of new entities created
 * @param existingEntitiesMapped Number of existing entities mapped
 * @returns Success messages in string array if any
 */
export function formatSuccessMessages(entitiesCreated: number, existingEntitiesMapped: number): string[] | undefined {
    if (entitiesCreated === 0 && existingEntitiesMapped === 0) {
        return undefined;
    }

    const successMessages: string[] = [];

    if (entitiesCreated) {
        successMessages.push(`${entitiesCreated} new Xero ${plural(entitiesCreated, 'entity', 'entities')} created.`);
    }

    if (existingEntitiesMapped) {
        successMessages.push(`${existingEntitiesMapped} existing ${plural(existingEntitiesMapped, 'entity', 'entities')} mapped to Xero.`);
    }

    return successMessages;
}

/**
 * Format request error into readable format in string array
 *
 * @param messages Error messages in an object
 * @returns Error messages in string array if any
 */
export function formatErrorMessages(messages?: { [entityName: string]: string[] }): string[] | undefined {
    if (!messages || Object.keys(messages).length === 0) {
        return undefined;
    }

    const errorMessages: string[] = [];
    for (const key in messages) {
        for (const i in messages[key]) {
            errorMessages.push(`${key} : ${messages[key][i]}`);
        }
    }

    return errorMessages;
}

type EntityConnections = {
    selectedEntities: OnboardingXeroEntity[];
    newEntities: XeroTenantConnection[];
    toUpdateEntities: OnboardingXeroEntity[];
};

/**
 * Split connections array into new entities to create and existing entities to map
 *
 * @param connections OnboardingXeroEntity[]
 * @returns New and to update entities
 */
export function groupXeroEntityConnections(connections: OnboardingXeroEntity[]): EntityConnections {
    return connections.reduce(
        (prev: EntityConnections, curr) => {
            if (curr.isSelected) {
                prev.selectedEntities.push(curr);

                if (curr.entityId) {
                    prev.toUpdateEntities.push(curr);
                } else {
                    prev.newEntities.push(curr.xeroEntity);
                }
            }

            return prev;
        },
        { selectedEntities: [], newEntities: [], toUpdateEntities: [] }
    );
}

/**
 * Format summary messages based on form value to indicate number of entities to be created/updated
 *
 * @param formValue Onboard entities form value
 * @returns Summary messages
 */
export function formatSummaryMessages(connections: OnboardingXeroEntity[], showAuthorisedEntityCount = true): string[] {
    const authorisedEntitiesCount = connections.length;

    const { newEntities, toUpdateEntities } = groupXeroEntityConnections(connections);
    const newEntitiesCount = newEntities.length;
    const toUpdateEntitiesCount = toUpdateEntities.length;

    const summaryMessages: string[] = [];

    if (showAuthorisedEntityCount && authorisedEntitiesCount > 0) {
        summaryMessages.push(`${authorisedEntitiesCount} Xero ${plural(authorisedEntitiesCount, 'entity', 'entities')} authorised.`);
    }

    if (newEntitiesCount > 0) {
        summaryMessages.push(`${newEntitiesCount} Xero ${plural(newEntitiesCount, 'entity', 'entities')} will be created.`);
    }

    if (toUpdateEntitiesCount > 0) {
        summaryMessages.push(
            `${toUpdateEntitiesCount} existing ${plural(toUpdateEntitiesCount, 'entity', 'entities')} will be mapped to Xero.`
        );
    }

    return summaryMessages;
}

/**
 * Search for a matching entity by name in entity array
 * https://www.doabledanny.com/binary-search-javascript
 *
 * @param arr Entity[]
 * @param target Entity name
 * @returns Entity id if a match is found
 */
export function binarySearchEntityName(arr: Entity[], target: string): string | undefined {
    let start = 0;
    let end = arr.length - 1;

    while (start <= end) {
        const middle = Math.floor((start + end) / 2);

        if (arr[middle].name < target) {
            // Search the right half
            start = middle + 1;
        } else if (arr[middle].name > target) {
            // Search the left half
            end = middle - 1;
        } else if (arr[middle].name === target) {
            // Found target
            return arr[middle].id;
        }
    }
}
